import React, { useMemo, useState } from 'react';
import {Helmet} from 'react-helmet';
import Style from '../styles/Gallery.module.css';
import images from '../components/PreloadImages';

const categories = [
  {
    id: 'paint',
    name: 'Paint',
    description: 'Interior and exterior finish work',
    images: images.paint,
  },
  {
    id: 'concrete',
    name: 'Concrete',
    description: 'Driveways, slabs, sidewalks, and repairs',
    images: images.concrete,
  },
  {
    id: 'roof',
    name: 'Roofing',
    description: 'Shingle, metal, repair, and replacement work',
    images: images.roof,
  },
  {
    id: 'deck',
    name: 'Decks/Fences',
    description: 'Outdoor living spaces, porches, decks, and fences',
    images: images.deck,
  },
  {
    id: 'stone',
    name: 'Stone Patio',
    description: 'Patios, stonework, and outdoor hardscapes',
    images: images.stone,
  },
  {
    id: 'block',
    name: 'Block Foundation',
    description: 'Foundation, wall, and block construction',
    images: images.block,
  },
];

const projects = categories.flatMap((category) =>
  category.images.map((image, index) => ({
    id: `${category.id}-${index}`,
    image,
    category: category.name,
    categoryId: category.id,
    description: category.description,
    title: `${category.name} Project ${index + 1}`,
  }))
);

function Gallery() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);

  const filteredProjects = useMemo(() => {
    if (selectedCategory === 'all') {
      return projects;
    }

    return projects.filter((project) => project.categoryId === selectedCategory);
  }, [selectedCategory]);

  const selectedCategoryName = selectedCategory === 'all'
    ? 'All Projects'
    : categories.find((category) => category.id === selectedCategory)?.name;

  return (
    <div>
      <Helmet>
        <title>Gallery - Dino Constructions</title>
        <meta name="description" content="Explore Dino Constructions project gallery and see concrete, roofing, decks, painting, stone patio, and block foundation work." />
        <meta property="og:title" content="Gallery - Dino Constructions" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dinoconstructions.com/Gallery" />
        <link rel="canonical" href="https://dinoconstructions.com/Gallery" />
        <meta property="og:description" content="Explore Dino Constructions project gallery and see concrete, roofing, decks, painting, stone patio, and block foundation work." />
      </Helmet>

      <section className={Style.Content1}>
        <div className={Style.Intro}>
          <p>Project gallery</p>
          <h1>Real work from real homes around Asheville</h1>
          <span>Browse completed projects by service type.</span>
        </div>
      </section>

      <section className={Style.GalleryShell}>
        <div className={Style.GalleryHeader}>
          <div>
            <p className={Style.sectionKicker}>Our work</p>
            <h2>{selectedCategoryName}</h2>
          </div>
          <p>{filteredProjects.length} photos</p>
        </div>

        <div className={Style.navigationMenu} aria-label="Gallery categories">
          <button
            className={selectedCategory === 'all' ? Style.active : ''}
            onClick={() => setSelectedCategory('all')}
            type="button"
          >
            <span>All</span>
            <small>{projects.length}</small>
          </button>
          {categories.map(category => (
            <button
              key={category.id}
              className={selectedCategory === category.id ? Style.active : ''}
              onClick={() => setSelectedCategory(category.id)}
              type="button"
            >
              <span>{category.name}</span>
              <small>{category.images.length}</small>
            </button>
          ))}
        </div>

        <div className={Style.ImgGrid}>
          {filteredProjects.map((project, index) => (
            <button
              className={`${Style.ProjectCard} ${index % 7 === 0 ? Style.FeaturedCard : ''}`}
              key={project.id}
              onClick={() => setSelectedProject(project)}
              type="button"
            >
              <img src={project.image} alt={project.title} loading="lazy" decoding="async" width="640" height="480" />
              <span>
                <strong>{project.category}</strong>
                <small>View photo</small>
              </span>
            </button>
          ))}
        </div>
      </section>

      {selectedProject && (
        <div className={Style.enlargedImgContainer} onClick={() => setSelectedProject(null)}>
          <div className={Style.LightboxPanel} onClick={(event) => event.stopPropagation()}>
            <button className={Style.CloseButton} type="button" onClick={() => setSelectedProject(null)}>Close</button>
            <img className={Style.enlargedImg} src={selectedProject.image} alt={selectedProject.title} />
            <div className={Style.LightboxMeta}>
              <p>{selectedProject.category}</p>
              <h2>{selectedProject.title}</h2>
              <span>{selectedProject.description}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Gallery;
